import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import EditSubscriptionsForm from "../../../components/forms/teamForms/editSubscription";
import { db, functions } from "../../../firebase-client";
import { CREATE_ACCOUNT_LINK_URL } from "../../../utils/functionURLs";
import {
  getMembershipHandling,
  isClubManagedMembershipMode,
  MEMBERSHIP_HANDLING_MODES,
  supportsOfflineMembershipPayments,
} from "../../../utils/membershipHandling";
import "./club-billing.css";

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : Promise.resolve(null);

const MODE_COPY = {
  hosted_stripe: [
    "Member Nest + Stripe",
    "Members pay through the existing automated Stripe membership flow.",
  ],
  hybrid: [
    "Member choice · online or offline",
    "Members can pay through Stripe for immediate access or wait for offline payment approval.",
  ],
  club_gated: [
    "Club-managed · gated",
    "Applicants wait outside the club until your administrators admit them.",
  ],
  club_pay_later: [
    "Club-managed · pay later",
    "Members enter after the normal joining process and settle with the club later.",
  ],
};

const money = (minor, currency = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(
    Number(minor || 0) / 100,
  );
const dateLabel = (value) =>
  value ? new Date(value).toLocaleDateString("en-GB") : "—";
const evidenceLabel = (member) => {
  if (member.included)
    return `Included after qualifying ${dateLabel(member.qualifiedAt)}`;
  if (member.reason === "stripe_coverage_active")
    return `Excluded while Stripe-covered until ${dateLabel(member.stripeCoveredUntil)}`;
  if (member.reason === "stripe_collection_route")
    return "Excluded: membership is collected online through Stripe";
  if (member.reason === "left_before_24_hour_qualification")
    return `Excluded: left before 24 hours (${dateLabel(member.joinedAt)} – ${dateLabel(member.leftAt)})`;
  return `Not yet included: 24-hour qualification runs from ${dateLabel(member.joinedAt)}`;
};

function AnnualAuditMembers({ members = [] }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const matching = useMemo(() => members
    .filter(member => (filter === "all" || (filter === "included" ? member.included : !member.included))
      && String(member.name || "Member").toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => String(a.name || "Member").localeCompare(String(b.name || "Member"))), [members, query, filter]);
  const pages = Math.max(1, Math.ceil(matching.length / pageSize));
  const currentPage = Math.min(page, pages);
  const start = (currentPage - 1) * pageSize;
  const included = members.filter(member => member.included).length;
  return <details className="club-billing-member-breakdown">
    <summary><strong>Member breakdown</strong><span>{included} included · {members.length - included} not included</span></summary>
    <div className="club-billing-audit-controls">
      <label>Search members<input className="form-input" type="search" value={query} placeholder="Search by name" onChange={event => { setQuery(event.target.value); setPage(1); }} /></label>
      <label>Inclusion<select className="form-input" value={filter} onChange={event => { setFilter(event.target.value); setPage(1); }}>
        <option value="all">All members</option><option value="included">Included in charge</option><option value="excluded">Not included in charge</option>
      </select></label>
    </div>
    <p className="text-muted" role="status">{matching.length ? `Showing ${start + 1}–${Math.min(start + pageSize, matching.length)} of ${matching.length} members` : "No members match this view."}</p>
    <ul className="club-billing-audit-members">
      {matching.slice(start, start + pageSize).map((member, index) => <li key={`${member.name}-${start + index}`}><strong>{member.name || "Member"}</strong><span>{evidenceLabel(member)}</span></li>)}
    </ul>
    {pages > 1 && <nav className="club-billing-audit-pagination" aria-label="Member breakdown pages">
      <button type="button" className="button button-secondary button--sm" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>Previous</button>
      <span>Page {currentPage} of {pages}</span>
      <button type="button" className="button button-secondary button--sm" disabled={currentPage === pages} onClick={() => setPage(currentPage + 1)}>Next</button>
    </nav>}
  </details>;
}

function CutoverWizard({
  fromMode,
  toMode,
  readiness,
  loading,
  error,
  effectiveAt,
  busy,
  onEffectiveAtChange,
  onRefresh,
  onConnectStripe,
  onEditPricing,
  onSetUpBillingCard,
  onClose,
  onConfirm,
}) {
  const [step, setStep] = useState(1);
  const [confirmed, setConfirmed] = useState(false);
  const movingToHosted = toMode === MEMBERSHIP_HANDLING_MODES.HOSTED_STRIPE;
  const movingToHybrid = toMode === MEMBERSHIP_HANDLING_MODES.HYBRID;
  const movingToManaged = isClubManagedMembershipMode(toMode);
  const readinessOkay = readiness?.ready === true;
  const futureDate = effectiveAt && new Date(effectiveAt).getTime() > Date.now();
  return (
    <div className="modal-backdrop club-cutover-backdrop" role="presentation">
      <section
        className="modal-content club-cutover-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cutover-title"
      >
        <div className="club-cutover-header">
          <div>
            <p className="club-billing-eyebrow">Guided membership cutover</p>
            <h2 id="cutover-title">
              Move to {MODE_COPY[toMode]?.[0] || "the selected option"}
            </h2>
          </div>
          <button
            type="button"
            className="club-cutover-close"
            onClick={onClose}
            aria-label="Close membership cutover"
            disabled={busy}
          >
            ×
          </button>
        </div>
        <ol className="club-cutover-steps" aria-label="Cutover progress">
          {["What changes", "Readiness", "Schedule"].map((label, index) => (
            <li className={step === index + 1 ? "active" : ""} key={label}>
              <span>{index + 1}</span>{label}
            </li>
          ))}
        </ol>

        {step === 1 && (
          <div className="club-cutover-panel">
            <p>
              You are changing from <strong>{MODE_COPY[fromMode]?.[0]}</strong>{" "}
              to <strong>{MODE_COPY[toMode]?.[0]}</strong>. Existing member
              access and payment history will be preserved.
            </p>
            {movingToHybrid ? (
              <ul>
                <li>Existing members keep their current online or offline payment route.</li>
                <li>New members can compare Stripe and offline totals before choosing.</li>
                <li>Stripe setup, membership pricing, offline instructions and an annual billing card must all be ready.</li>
              </ul>
            ) : movingToHosted ? (
              <ul>
                <li>New membership payments use Member Nest and Stripe from the effective date.</li>
                <li>Members who need a hosted payment arrangement will be prompted to set one up.</li>
                <li>The current club-managed billing period closes on that date and is charged on the next month-start.</li>
                <li>Your connected Stripe account and membership prices must be ready before scheduling.</li>
              </ul>
            ) : movingToManaged ? (
              <ul>
                <li>New Stripe memberships stop on the effective date.</li>
                <li>Existing Stripe renewals are cancelled at period end, without removing member access.</li>
                <li>Stripe-covered members enter the annual headcount only after their paid coverage expires.</li>
                <li>A saved annual Member Nest billing card is required.</li>
                <li>External membership prices or payment instructions must be ready.</li>
              </ul>
            ) : null}
          </div>
        )}

        {step === 2 && (
          <div className="club-cutover-panel" aria-busy={loading}>
            {loading ? (
              <p className="club-cutover-loading" role="status">Checking the club’s payment setup…</p>
            ) : (
              <>
              {(movingToHosted || movingToHybrid) && <>
                <div className="club-cutover-check">
                  <div><strong>Connected Stripe account</strong><small>Onboarding, charges, payouts and transfers must all be active.</small></div>
                  <span className={readiness?.stripe?.ready ? "ready" : "attention"}>{readiness?.stripe?.ready ? "Ready" : "Needs attention"}</span>
                </div>
                {!readiness?.stripe?.ready && (
                  <button type="button" className="button button-secondary" onClick={onConnectStripe}>Set up or review Stripe</button>
                )}
                <div className="club-cutover-check">
                  <div><strong>Membership prices and schedules</strong><small>These determine what members will pay through Stripe.</small></div>
                  <span className={readiness?.membershipPricing?.ready ? "ready" : "attention"}>{readiness?.membershipPricing?.ready ? "Ready" : "Needs attention"}</span>
                </div>
                <button type="button" className="button button-secondary" onClick={onEditPricing}>
                  {readiness?.membershipPricing?.ready ? "Review membership pricing" : "Set up membership pricing"}
                </button>
              </>}
              {(movingToManaged || movingToHybrid) && <>
                <div className="club-cutover-check">
                  <div><strong>Annual Member Nest billing card</strong><small>The saved card will be used for the club’s annual headcount charge.</small></div>
                  <span className={readiness?.billingCard?.ready ? "ready" : "attention"}>{readiness?.billingCard?.ready ? "Ready" : "Needs attention"}</span>
                </div>
                {!readiness?.billingCard?.ready && (
                  <button type="button" className="button button-secondary" onClick={onSetUpBillingCard}>Set up annual billing card</button>
                )}
                <div className="club-cutover-check">
                  <div><strong>External payment details</strong><small>{movingToHybrid ? "Members see the shared membership price and instructions for paying the club offline." : "Choose whether members see prices, instructions, or both."}</small></div>
                  <span className={readiness?.externalPayment?.ready ? "ready" : "attention"}>{readiness?.externalPayment?.ready ? "Ready" : "Needs attention"}</span>
                </div>
                <button type="button" className="button button-secondary" onClick={onEditPricing}>
                  {readiness?.externalPayment?.ready ? "Review external payment details" : "Set up external payment details"}
                </button>
              </>}
              </>
            )}
            {error && <p className="modal-error" role="alert">{error}</p>}
            {!loading && <button type="button" className="button button-ghost" onClick={onRefresh}>Check again</button>}
          </div>
        )}

        {step === 3 && (
          <div className="club-cutover-panel">
            <label className="form-group">
              <span>Effective date and time</span>
              <input className="form-input" type="datetime-local" value={effectiveAt} onChange={(event) => onEffectiveAtChange(event.target.value)} />
            </label>
            <p className="text-muted">Nothing changes immediately. The cutover is scheduled for this time and can be cancelled beforehand.</p>
            <label className="club-billing-check club-cutover-confirm">
              <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
              I understand the payment and billing changes above and want to schedule this cutover.
            </label>
            {error && <p className="modal-error" role="alert">{error}</p>}
          </div>
        )}

        <div className="club-cutover-actions">
          <button type="button" className="button button-secondary" onClick={step === 1 ? onClose : () => setStep(step - 1)} disabled={busy}>
            {step === 1 ? "Cancel" : "Back"}
          </button>
          {step < 3 ? (
            <button type="button" className="button button-primary" onClick={() => setStep(step + 1)} disabled={step === 2 && (!readinessOkay || loading)}>
              Continue
            </button>
          ) : (
            <button type="button" className="button button-primary" onClick={onConfirm} disabled={busy || !confirmed || !futureDate}>
              {busy ? "Scheduling…" : "Schedule cutover"}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

function ClubBillingLoading({ reconciliationOnly }) {
  const label = reconciliationOnly
    ? "membership reconciliation"
    : "membership payments and billing";
  return (
    <main className="club-billing-page club-billing-loading" aria-busy="true">
      <header>
        <div className="club-billing-loading__heading">
          <span className="club-billing-loading__line club-billing-loading__line--eyebrow" />
          <span className="club-billing-loading__line club-billing-loading__line--title" />
        </div>
      </header>
      <p className="club-billing-loading__status" role="status" aria-live="polite">
        Loading {label}…
      </p>
      <section className="club-billing-grid" aria-hidden="true">
        {[0, 1].map((item) => (
          <article className="card club-billing-loading__card" key={item}>
            <span className="club-billing-loading__line club-billing-loading__line--card-title" />
            <span className="club-billing-loading__line" />
            <span className="club-billing-loading__line club-billing-loading__line--short" />
            <span className="club-billing-loading__block" />
          </article>
        ))}
      </section>
    </main>
  );
}

function BillingCardSetup({
  teamId,
  billingName,
  initialPostalCode,
  onSaved,
  onSavingChange,
  setupRef,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [postalCode, setPostalCode] = useState(initialPostalCode || "");
  const [billingAuthorised, setBillingAuthorised] = useState(false);
  const [saveStage, setSaveStage] = useState("");
  const savingRef = useRef(false);
  useEffect(() => {
    setPostalCode(initialPostalCode || "");
  }, [initialPostalCode]);
  const save = async () => {
    if (!stripe || !elements || savingRef.current || !billingAuthorised) return;
    savingRef.current = true;
    setBusy(true);
    onSavingChange(true);
    setSaveStage("Preparing secure card setup…");
    setError("");
    try {
      const create = httpsCallable(functions, "createClubBillingSetupIntent");
      const result = await create({ teamId });
      setSaveStage("Saving your card securely with Stripe…");
      const confirmed = await stripe.confirmCardSetup(
        result.data.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              ...(billingName ? { name: billingName } : {}),
              ...(postalCode.trim()
                ? { address: { postal_code: postalCode.trim() } }
                : {}),
            },
          },
        },
      );
      if (confirmed.error) throw confirmed.error;
      setSaveStage("Confirming billing authorisation…");
      const finalize = httpsCallable(functions, "finalizeClubBillingCard");
      await finalize({
        teamId,
        setupIntentId: confirmed.setupIntent.id,
        billingAuthorisationAccepted: billingAuthorised,
      });
      setSaveStage("Updating billing details…");
      await onSaved?.();
    } catch (err) {
      setError(err?.message || "Could not save this billing card.");
    } finally {
      savingRef.current = false;
      setBusy(false);
      onSavingChange(false);
      setSaveStage("");
    }
  };
  return (
    <div className="club-billing-card-form" ref={setupRef} aria-busy={busy}>
      {busy && (
        <p className="club-billing-info" role="status" aria-live="polite">
          {saveStage} Please wait before making other changes.
        </p>
      )}
      <CardElement options={{ hidePostalCode: true, disabled: busy }} />
      <label className="club-billing-postal-code">
        <span>Billing postcode or ZIP</span>
        <input
          className="form-input"
          type="text"
          inputMode="text"
          autoComplete="postal-code"
          value={postalCode}
          onChange={(event) => setPostalCode(event.target.value)}
          placeholder="For example, CM1 1AA"
          maxLength={20}
          disabled={busy}
        />
        <small>
          Letters, numbers, spaces and international formats are accepted.
        </small>
      </label>
      {error && (
        <p className="modal-error" role="alert">
          {error}
        </p>
      )}
      <label className="club-billing-authorisation">
        <input
          type="checkbox"
          checked={billingAuthorised}
          onChange={(event) => setBillingAuthorised(event.target.checked)}
          disabled={busy}
        />
        <span>
          I understand and authorise Member Nest to charge this card after each
          12-month period, or when the club closes earlier, for qualifying club
          accounts at the rate shown in the club's billing audit.
        </span>
      </label>
      <button
        type="button"
        className="button button-primary"
        onClick={save}
        disabled={!stripe || busy || !billingAuthorised}
      >
        {busy ? "Saving…" : "Save billing card"}
      </button>
      <small>
        Card details are held by Stripe for off-session annual Member Nest
        charges.
      </small>
    </div>
  );
}

function downloadAudit(overview) {
  const rows = [
    ["Period", "Member", "Qualified", "Included", "Reason", "Rate", "Currency"],
  ];
  (overview?.periods || []).forEach((period) =>
    (period.members || []).forEach((member) =>
      rows.push([
        period.id,
        member.name,
        member.qualifiedAt || "",
        member.included ? member.includedAt || "" : "Excluded",
        member.reason || "",
        period.rateMinor,
        period.currency,
      ]),
    ),
  );
  const csv = rows
    .map((row) =>
      row
        .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
        .join(","),
    )
    .join("\n");
  const url = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = "member-nest-billing-audit.csv";
  link.click();
  URL.revokeObjectURL(url);
}

function downloadReconciliation(reconciliation) {
  const rounds = new Map(
    (reconciliation?.rounds || []).map((round) => [round.id, round]),
  );
  const rows = [
    [
      "Member",
      "Round",
      "Status",
      "Expected",
      "Paid",
      "Currency",
      "Paid date",
      "Method",
      "Reference",
    ],
  ];
  (reconciliation?.records || []).forEach((record) => {
    const round = rounds.get(record.roundId) || {};
    rows.push([
      record.memberName,
      round.name || "",
      record.status,
      record.expectedAmountMinor,
      record.paidAmountMinor,
      round.currency || "GBP",
      record.paidAt || "",
      record.method || "",
      record.reference || "",
    ]);
  });
  const csv = rows
    .map((row) =>
      row
        .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
        .join(","),
    )
    .join("\n");
  const url = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = "membership-reconciliation.csv";
  link.click();
  URL.revokeObjectURL(url);
}

export default function ClubBillingPage() {
  const { teamId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const reconciliationOnly = location.pathname.endsWith(
    "/membership-reconciliation",
  );
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [canFinance, setCanFinance] = useState(false);
  const [canManageMembership, setCanManageMembership] = useState(false);
  const [overview, setOverview] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [mode, setMode] = useState(MEMBERSHIP_HANDLING_MODES.HOSTED_STRIPE);
  const [requiresAssignment, setRequiresAssignment] = useState(false);
  const [effectiveAt, setEffectiveAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [replacingBillingCard, setReplacingBillingCard] = useState(false);
  const [cardSaving, setCardSaving] = useState(false);
  const cardSetupRef = useRef(null);
  const attemptedModeRef = useRef(null);

  useEffect(() => {
    if (!cardSaving || !cardSetupRef.current) return;
    // Keep the form and Stripe authentication available while disabling the
    // surrounding page and every navigation layout (including PWA menus).
    const disabledElements = [];
    let current = cardSetupRef.current;
    while (current.parentElement) {
      for (const sibling of current.parentElement.children) {
        if (sibling !== current && !sibling.hasAttribute("inert")) {
          sibling.setAttribute("inert", "");
          disabledElements.push(sibling);
        }
      }
      current = current.parentElement;
      if (current === document.body) break;
    }
    const preventUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", preventUnload);
    return () => {
      disabledElements.forEach((element) => element.removeAttribute("inert"));
      window.removeEventListener("beforeunload", preventUnload);
    };
  }, [cardSaving]);
  const [cutoverOpen, setCutoverOpen] = useState(false);
  const [cutoverReadiness, setCutoverReadiness] = useState(null);
  const [cutoverLoading, setCutoverLoading] = useState(false);
  const [cutoverError, setCutoverError] = useState("");
  const [pricingEditorMode, setPricingEditorMode] = useState(
    MEMBERSHIP_HANDLING_MODES.HOSTED_STRIPE,
  );
  const [pricingSetupForCutover, setPricingSetupForCutover] = useState(false);
  const [stripeSetupLoading, setStripeSetupLoading] = useState(false);
  const [resumedCutover, setResumedCutover] = useState(false);
  const [changingHandling, setChangingHandling] = useState(false);
  const [roundDraft, setRoundDraft] = useState({
    name: "",
    expectedAmountMajor: "",
    dueDate: "",
    instructions: "",
  });
  const [assignmentRoundId, setAssignmentRoundId] = useState("");
  const [candidateSearch, setCandidateSearch] = useState("");
  const [selectedCandidateKeys, setSelectedCandidateKeys] = useState([]);
  const [recordDrafts, setRecordDrafts] = useState({});
  const [recordFilter, setRecordFilter] = useState("all");
  const [recordSearch, setRecordSearch] = useState("");
  const handling = useMemo(() => getMembershipHandling(team || {}), [team]);
  const isFullAdmin = Boolean(user?.uid && team?.admins?.includes(user.uid));
  const requestedSetupMode = searchParams.get("setupMode");
  const requiresGuidedCutover =
    team?.checklist?.access?.completed === true && mode !== handling.mode;
  const currentClubManaged = isClubManagedMembershipMode(handling.mode);
  const showAnnualBillingCard =
    supportsOfflineMembershipPayments(handling.mode) ||
    supportsOfflineMembershipPayments(mode);
  const showAnnualAudit =
    supportsOfflineMembershipPayments(handling.mode) || (overview?.periods || []).length > 0;
  useEffect(() => {
    if (!pricingSetupForCutover) setPricingEditorMode(handling.mode);
  }, [handling.mode, pricingSetupForCutover]);

  const load = async ({ resetDraft = false } = {}) => {
    if (!teamId || !user?.uid) return;
    const [teamSnap, assignmentSnap] = await Promise.all([
      getDoc(doc(db, "teams", teamId)),
      getDoc(doc(db, "teams", teamId, "adminAssignments", user.uid)).catch(
        () => null,
      ),
    ]);
    const data = teamSnap.data() || {};
    const finance =
      data.admins?.includes(user.uid) ||
      assignmentSnap?.data()?.permissionKeys?.includes("finance.manage");
    const membership =
      data.admins?.includes(user.uid) ||
      assignmentSnap?.data()?.permissionKeys?.includes("membership.manage");
    setTeam({ id: teamSnap.id, ...data });
    setCanFinance(Boolean(finance));
    setCanManageMembership(Boolean(membership));
    const nextHandling = getMembershipHandling(data);
    const requestedMode = Object.values(MEMBERSHIP_HANDLING_MODES).includes(
      requestedSetupMode,
    )
      ? requestedSetupMode
      : nextHandling.mode;
    if (resetDraft) {
      setMode(requestedMode);
      setChangingHandling(requestedMode !== nextHandling.mode);
      setRequiresAssignment(nextHandling.payLaterRequiresRoundAssignment);
    }
    if (finance) {
      if (reconciliationOnly) {
        const result = await httpsCallable(
          functions,
          "getMembershipReconciliation",
        )({ teamId });
        setReconciliation(result.data);
      } else {
        const result = await httpsCallable(
          functions,
          "getClubBillingOverview",
        )({ teamId });
        setOverview(result.data);
      }
    }
  };
  useEffect(() => {
    setPageLoading(true);
    setError("");
    load({ resetDraft: true })
      .catch((err) =>
        setError(err?.message || "Could not load club billing."),
      )
      .finally(() => setPageLoading(false));
  }, [teamId, user?.uid, reconciliationOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMode = async ({ guidedCutover = false } = {}) => {
    setBusy(true);
    setError("");
    setCutoverError("");
    try {
      await httpsCallable(
        functions,
        "setMembershipHandlingMode",
      )({
        teamId,
        mode,
        payLaterRequiresRoundAssignment: requiresAssignment,
        ...(requiresGuidedCutover ? { effectiveAt: new Date(effectiveAt).toISOString() } : {}),
        ...(guidedCutover
          ? {
              guidedCutover: {
                confirmed: true,
                version: "membership_cutover_v1",
              },
            }
          : {}),
      });
      await load();
      setChangingHandling(false);
      if (guidedCutover) setCutoverOpen(false);
    } catch (err) {
      const message = err?.message || "Could not update membership handling.";
      if (guidedCutover) setCutoverError(message);
      else setError(message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (pageLoading || !isFullAdmin || !changingHandling || requiresGuidedCutover
      || mode === handling.mode || busy || cardSaving
      || (supportsOfflineMembershipPayments(mode) && overview?.card?.healthy !== true)) return undefined;
    const signature = JSON.stringify({ mode, requiresAssignment });
    if (attemptedModeRef.current === signature) return undefined;
    const timer = window.setTimeout(() => {
      attemptedModeRef.current = signature;
      saveMode();
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [pageLoading, isFullAdmin, changingHandling, requiresGuidedCutover, mode, handling.mode, requiresAssignment, busy, cardSaving, overview?.card?.healthy]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkCutoverReadiness = async () => {
    if (!mode || mode === handling.mode) return;
    setCutoverLoading(true);
    setCutoverError("");
    try {
      const result = await httpsCallable(
        functions,
        "getMembershipCutoverReadiness",
      )({ teamId, targetMode: mode });
      setCutoverReadiness(result.data);
    } catch (err) {
      setCutoverReadiness(null);
      setCutoverError(err?.message || "Could not check payment readiness.");
    } finally {
      setCutoverLoading(false);
    }
  };
  const openCutover = () => {
    setError("");
    setCutoverError("");
    setCutoverReadiness(null);
    if (!effectiveAt) {
      const defaultDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
      defaultDate.setMinutes(defaultDate.getMinutes() - defaultDate.getTimezoneOffset());
      setEffectiveAt(defaultDate.toISOString().slice(0, 16));
    }
    setCutoverOpen(true);
    checkCutoverReadiness();
  };
  const connectStripe = async () => {
    if (!user || stripeSetupLoading) return;
    setStripeSetupLoading(true);
    setCutoverError("");
    try {
      const token = await user.getIdToken();
      const response = await fetch(CREATE_ACCOUNT_LINK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ teamId, returnToClubBilling: true }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || "Unable to open Stripe.");
      if (!data?.url) throw new Error("Stripe setup link is not available.");
      window.location.href = data.url;
    } catch (err) {
      setCutoverError(err?.message || "Unable to open Stripe.");
      setStripeSetupLoading(false);
    }
  };
  useEffect(() => {
    if (
      !pageLoading &&
      team &&
      searchParams.get("resumeCutover") === "1" &&
      mode !== handling.mode &&
      !cutoverOpen &&
      !resumedCutover
    ) {
      setResumedCutover(true);
      openCutover();
    }
  }, [pageLoading, team, mode, handling.mode, cutoverOpen, resumedCutover]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancelTransition = async () => {
    setBusy(true);
    setError("");
    try {
      await httpsCallable(
        functions,
        "setMembershipHandlingMode",
      )({
        teamId,
        mode: handling.mode,
        cancelScheduledTransition: true,
      });
      await load();
    } catch (err) {
      setError(err?.message || "Could not cancel the scheduled change.");
    } finally {
      setBusy(false);
    }
  };
  const createRound = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await httpsCallable(
        functions,
        "saveMembershipPaymentRound",
      )({
        teamId,
        name: roundDraft.name,
        state: "open",
        currency: reconciliation?.rounds?.[0]?.currency || "GBP",
        expectedAmountMinor: Math.round(
          Number(roundDraft.expectedAmountMajor || 0) * 100,
        ),
        dueDate: roundDraft.dueDate || null,
        instructions: roundDraft.instructions,
      });
      setRoundDraft({
        name: "",
        expectedAmountMajor: "",
        dueDate: "",
        instructions: "",
      });
      await load();
    } catch (err) {
      setError(err?.message || "Could not create payment round.");
    } finally {
      setBusy(false);
    }
  };
  const mark = async (record, status) => {
    setBusy(true);
    setError("");
    const draft = recordDrafts[record.key] || {};
    try {
      await httpsCallable(
        functions,
        "updateMembershipPaymentRecord",
      )({
        teamId,
        recordKey: record.key,
        status,
        paidAmountMinor:
          status === "paid"
            ? Math.round(
                Number(
                  draft.paidAmountMajor ?? record.expectedAmountMinor / 100,
                ) * 100,
              )
            : 0,
        paidAt: draft.paidAt || null,
        method: draft.method || "",
        reference: draft.reference || "",
        adminNote: draft.adminNote || "",
      });
      await load();
    } catch (err) {
      setError(err?.message || "Could not update payment.");
    } finally {
      setBusy(false);
    }
  };
  const visibleRecords = (reconciliation?.records || []).filter((record) => {
    const matchesStatus =
      recordFilter === "all" ||
      (recordFilter === "outstanding"
        ? record.status === "unpaid"
        : record.status === recordFilter);
    return (
      matchesStatus &&
      record.memberName
        .toLowerCase()
        .includes(recordSearch.trim().toLowerCase())
    );
  });
  const availableCandidates = (reconciliation?.candidates || []).filter(
    (candidate) =>
      !(reconciliation?.records || []).some(
        (record) =>
          record.roundId === assignmentRoundId &&
          record.candidateKey === candidate.key,
      ),
  );
  const shownCandidates = availableCandidates.filter((candidate) =>
    candidate.name.toLowerCase().includes(candidateSearch.trim().toLowerCase()),
  );
  const roundRecords = (reconciliation?.records || []).filter(
    (record) => record.roundId === assignmentRoundId,
  );
  const outstandingRoundRecords = roundRecords.filter(
    (record) => record.status === "unpaid",
  );
  const toggleCandidate = (candidateKey) => {
    setSelectedCandidateKeys((current) =>
      current.includes(candidateKey)
        ? current.filter((key) => key !== candidateKey)
        : [...current, candidateKey],
    );
  };
  const assignCandidates = async (candidateKeys) => {
    if (!assignmentRoundId || !candidateKeys.length) return;
    setBusy(true);
    setError("");
    try {
      await httpsCallable(
        functions,
        "bulkAssignMembershipPaymentRecords",
      )({
        teamId,
        roundId: assignmentRoundId,
        candidateKeys,
      });
      setSelectedCandidateKeys([]);
      await load();
    } catch (err) {
      setError(err?.message || "Could not add people to this payment round.");
    } finally {
      setBusy(false);
    }
  };
  const removeOutstandingAssignments = async () => {
    if (!assignmentRoundId || !outstandingRoundRecords.length) return;
    const confirmed = window.confirm(
      `Remove all ${outstandingRoundRecords.length} outstanding assignment${outstandingRoundRecords.length === 1 ? "" : "s"} from this round? Paid and waived records will be kept.`,
    );
    if (!confirmed) return;
    setBusy(true);
    setError("");
    try {
      await httpsCallable(
        functions,
        "removeOutstandingMembershipPaymentRecords",
      )({ teamId, roundId: assignmentRoundId });
      setSelectedCandidateKeys([]);
      await load();
    } catch (err) {
      setError(err?.message || "Could not remove outstanding assignments.");
    } finally {
      setBusy(false);
    }
  };
  const setRoundState = async (round, state) => {
    setBusy(true);
    setError("");
    try {
      await httpsCallable(
        functions,
        "saveMembershipPaymentRound",
      )({
        teamId,
        roundId: round.id,
        name: round.name,
        state,
        currency: round.currency,
        expectedAmountMinor: round.expectedAmountMinor,
        dueDate: round.dueDate,
        instructions: round.instructions,
      });
      await load();
    } catch (err) {
      setError(err?.message || "Could not update the payment round.");
    } finally {
      setBusy(false);
    }
  };

  if (pageLoading)
    return <ClubBillingLoading reconciliationOnly={reconciliationOnly} />;
  if (!team)
    return (
      <main className="club-billing-page">
        <h1>{reconciliationOnly ? "Membership reconciliation" : "Club billing"}</h1>
        <p className="modal-error" role="alert">
          {error || "Club billing could not be loaded."}
        </p>
        <Link to={`/teams/${teamId}`}>Return to club</Link>
      </main>
    );
  if (
    (reconciliationOnly && !canFinance) ||
    (!reconciliationOnly && !canFinance && !canManageMembership)
  )
    return (
      <main className="club-billing-page">
        <h1>
          {reconciliationOnly ? "Membership reconciliation" : "Club billing"}
        </h1>
        <p>
          {reconciliationOnly
            ? "You need Payments and finance permission to view this area."
            : "You need Membership management or Payments and finance permission to view this area."}
        </p>
        <Link to={`/teams/${teamId}`}>Return to club</Link>
      </main>
    );
  return (
    <>
    <main className="club-billing-page">
      <header>
        <div>
          <p className="club-billing-eyebrow">{team.name}</p>
          <h1>
            {reconciliationOnly
              ? "Membership reconciliation"
              : "Membership payments and billing"}
          </h1>
          {!reconciliationOnly && <p className="club-billing-intro">Manage how members pay the club, your membership plans, and the club’s charges from Member Nest.</p>}
        </div>
        <Link className="button button-secondary" to={`/teams/${teamId}`}>
          Back to club
        </Link>
      </header>
      {error && (
        <p className="modal-error" role="alert">
          {error}
        </p>
      )}
      {(overview?.locked || team.billingAccess?.locked === true) && (
        <section className="club-billing-warning" role="alert">
          <strong>Club access is restricted</strong>
          <p>
            {overview?.lockReason ||
              team.billingAccess?.reason ||
              "Update the billing card and contact Member Nest support to resolve the outstanding charge."}
          </p>
        </section>
      )}

      {!reconciliationOnly && (
        <>
          <nav className="club-billing-section-nav" aria-label="Billing sections">
            {!overview?.locked && <a href="#payment-setup"><strong>Payment setup</strong><span>Current payment arrangement</span></a>}
            {(!currentClubManaged || canManageMembership || pricingSetupForCutover) && <a href="#membership-plans-and-fees"><strong>Membership plans</strong><span>Prices, schedules and payment details</span></a>}
            {canFinance && (showAnnualBillingCard || showAnnualAudit) && <a href="#member-nest-charges"><strong>Member Nest charges</strong><span>Club billing card and annual audit</span></a>}
          </nav>
          <section className="club-billing-grid" aria-label="Billing settings">
            {!overview?.locked && (
              <article className="card club-billing-handling-card" id="payment-setup" aria-labelledby="payment-setup-heading">
                <h2 id="payment-setup-heading">Payment setup</h2>
                <div className="club-billing-handling-summary">
                  <div className="club-billing-current-mode">
                    <span className="club-billing-current-mode__label">Current arrangement</span>
                    <div className="club-billing-current-mode__name">
                      <strong>{MODE_COPY[handling.mode]?.[0]}</strong>
                      <span aria-hidden="true">·</span>
                      <p>{MODE_COPY[handling.mode]?.[1]}</p>
                    </div>
                  </div>
                  {isFullAdmin && !changingHandling && !handling.scheduledTransition && (
                    <button
                      type="button"
                      className="button button-secondary"
                      onClick={() => setChangingHandling(true)}
                    >
                      Change payment handling
                    </button>
                  )}
                </div>
                {handling.scheduledTransition && (
                  <div className="club-billing-scheduled-change">
                    <p className="text-muted">
                      A change to{" "}
                      {MODE_COPY[handling.scheduledTransition.toMode]?.[0] ||
                        handling.scheduledTransition.toMode}{" "}
                      is scheduled for{" "}
                      {new Date(
                        handling.scheduledTransition.effectiveAt?.toDate?.() ||
                          handling.scheduledTransition.effectiveAt,
                      ).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                      .
                    </p>
                    {isFullAdmin && (
                      <button
                        type="button"
                        className="button button-secondary"
                        onClick={cancelTransition}
                        disabled={busy}
                      >
                        Cancel scheduled change
                      </button>
                    )}
                  </div>
                )}
                {changingHandling && (
                  <div className="club-billing-change-mode">
                    <h3>Choose a new arrangement</h3>
                    <div className="club-billing-modes">
                      {Object.entries(MODE_COPY).map(([value, copy]) => (
                        <label key={value} className={mode === value ? "selected" : ""}>
                          <input
                            type="radio"
                            name="mode"
                            value={value}
                            checked={mode === value}
                            disabled={busy || cardSaving}
                            onChange={() => {
                              attemptedModeRef.current = null;
                              setMode(value);
                              setError("");
                            }}
                          />
                          <span><strong>{copy[0]}</strong><small>{copy[1]}</small></span>
                        </label>
                      ))}
                    </div>
                    {mode === MEMBERSHIP_HANDLING_MODES.CLUB_PAY_LATER &&
                      team.modules?.membershipReconciliation?.enabled === true && (
                        <label className="club-billing-check">
                          <input
                            type="checkbox"
                            checked={requiresAssignment}
                            disabled={busy || cardSaving}
                            onChange={(event) => setRequiresAssignment(event.target.checked)}
                          />
                          Require a payment-round assignment before access
                        </label>
                      )}
                    {requiresGuidedCutover && (
                      <p className="club-billing-info">
                        Nothing changes immediately. A guided review covers
                        existing memberships, readiness and the effective date.
                      </p>
                    )}
                    {supportsOfflineMembershipPayments(mode) && overview?.card?.healthy !== true && (
                      <p className="club-billing-warning">
                        {requiresGuidedCutover
                          ? "The guided review includes annual billing-card setup before anything is scheduled."
                          : "Save the annual Member Nest billing card below before confirming this option."}
                      </p>
                    )}
                    <div className="club-billing-change-actions">
                      <button
                        type="button"
                        className="button button-secondary"
                        onClick={() => {
                          setMode(handling.mode);
                          setRequiresAssignment(handling.payLaterRequiresRoundAssignment);
                          setChangingHandling(false);
                          setError("");
                        }}
                        disabled={busy}
                      >
                        Cancel
                      </button>
                      {requiresGuidedCutover ? <button
                        type="button"
                        className="button button-primary"
                        onClick={openCutover}
                        disabled={busy || cardSaving || mode === handling.mode}
                      >
                        Review change
                      </button> : <p className="text-muted" role="status">
                        {busy ? 'Saving payment arrangement…'
                          : supportsOfflineMembershipPayments(mode) && overview?.card?.healthy !== true
                            ? 'Your choice will save automatically after the billing card is saved.'
                            : 'Your payment choice saves automatically.'}
                      </p>}
                      {!requiresGuidedCutover && error && <button
                        type="button"
                        className="button button-primary"
                        onClick={() => saveMode()}
                        disabled={busy || cardSaving}
                      >Retry payment choice</button>}
                    </div>
                  </div>
                )}
                {handling.mode === MEMBERSHIP_HANDLING_MODES.HYBRID && canFinance && (
                  <div className="club-billing-change-actions">
                    <button type="button" className="button button-secondary" onClick={connectStripe} disabled={stripeSetupLoading}>
                      {stripeSetupLoading ? "Opening Stripe…" : "Set up or review Stripe"}
                    </button>
                  </div>
                )}
              </article>
            )}

            {(!currentClubManaged || canManageMembership || pricingSetupForCutover) && (
            <article className="card club-billing-plans" id="membership-plans-and-fees">
                <h2>Membership plans</h2>
                <p>
                  {handling.mode === MEMBERSHIP_HANDLING_MODES.HYBRID && !pricingSetupForCutover
                    ? "Set the shared membership price, online fee handling, and the instructions shown to members who choose offline payment."
                    : currentClubManaged && !pricingSetupForCutover
                    ? "Set what members see about prices and how they should pay the club outside Member Nest."
                    : "Review the prices, payment schedule, family plans and fee handling used when Member Nest collects membership payments through Stripe."}
                </p>
                {pricingSetupForCutover && (
                  <p className="club-billing-info">
                    Complete the hosted membership pricing needed for your
                    planned change. After saving, the readiness review will reopen.
                  </p>
                )}
                {canManageMembership && (
                  <details className="club-billing-plan-editor" open={pricingSetupForCutover}>
                    <summary>Edit membership plans and payment details</summary>
                    <EditSubscriptionsForm
                    isOpen
                    embedded
                    autoSave
                    teamId={teamId}
                    membershipMode={pricingEditorMode}
                    onClose={() => {}}
                    onSave={async () => {
                      await updateDoc(doc(db, "teams", teamId), {
                        "checklist.profile": {
                          completed: true,
                          updatedAt: serverTimestamp(),
                        },
                      });
                      if (pricingSetupForCutover) {
                        await checkCutoverReadiness();
                        setPricingSetupForCutover(false);
                        setCutoverOpen(true);
                      }
                    }}
                    />
                  </details>
                )}
                {!canManageMembership && <p className="text-muted">Membership management permission is required to edit these plans.</p>}
              </article>
            )}

            {canFinance && (showAnnualBillingCard || showAnnualAudit) && <section className="club-billing-platform-section" id="member-nest-charges" aria-labelledby="member-nest-charges-heading">
              <header className="club-billing-platform-heading">
                <h2 id="member-nest-charges-heading">Member Nest charges</h2>
                <p>Charges paid by the club to Member Nest for qualifying offline members. Member payments to the club are managed above.</p>
              </header>
            {canFinance && showAnnualBillingCard && (
            <article className="card" id="annual-billing-card">
              <h3>Club billing card</h3>
              {overview?.card && !replacingBillingCard ? (
                <div
                  className={`club-billing-saved-card${overview.card.healthy ? "" : " club-billing-saved-card--attention"}`}
                  role="status"
                >
                  <div className="club-billing-saved-card__details">
                    <span className="club-billing-saved-card__icon" aria-hidden="true">
                      {overview.card.healthy ? "✓" : "!"}
                    </span>
                    <span>
                      <strong>
                        {overview.card.healthy
                          ? "Billing card saved"
                          : "Billing card needs replacing"}
                      </strong>
                      <small>
                        {overview.card.brand?.toUpperCase()} •••• {overview.card.last4}
                        {" · "}Expires {overview.card.expMonth}/{overview.card.expYear}
                      </small>
                    </span>
                  </div>
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={() => setReplacingBillingCard(true)}
                  >
                    Replace billing card
                  </button>
                </div>
              ) : !overview?.card ? (
                <p className="text-muted">
                  A billing card is required before offline membership payment can go live.
                </p>
              ) : null}
              {(!overview?.card || replacingBillingCard) && (
                <div className="club-billing-card-editor">
                  {replacingBillingCard && (
                    <div className="club-billing-card-editor__heading">
                      <strong>Replace billing card</strong>
                      <button
                        type="button"
                        className="button button-secondary button--sm"
                        onClick={() => setReplacingBillingCard(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  <Elements stripe={stripePromise}>
                    <BillingCardSetup
                      teamId={teamId}
                      billingName={team.name}
                      initialPostalCode={team.address?.postcode || ""}
                      setupRef={cardSetupRef}
                      onSavingChange={setCardSaving}
                      onSaved={async () => {
                        const result = await httpsCallable(
                          functions,
                          "getClubBillingOverview",
                        )({ teamId });
                        setOverview(result.data);
                        setReplacingBillingCard(false);
                      }}
                    />
                  </Elements>
                </div>
              )}
              <details className="club-billing-explanation">
                <summary>How annual charges are calculated</summary>
                <ul>
                  <li>
                    A 12-month billing period begins when the club goes live
                    using offline membership payments, or switches to an arrangement that supports them.
                  </li>
                  <li>
                    At the start of that period, the rate is fixed at the
                    configured local-currency equivalent of £1 per qualifying
                    account.
                  </li>
                  <li>
                    An account qualifies after 24 continuous hours in the club.
                    {(supportsOfflineMembershipPayments(handling.mode) ? handling.mode : mode) === MEMBERSHIP_HANDLING_MODES.HYBRID
                      ? " Only accounts using offline membership payment count; Stripe-paid accounts do not."
                      : " Admins, managed children and course-only members count; shorter joins do not."}
                  </li>
                  <li>
                    Member Nest charges this card on the first day of the month
                    after the period ends. If the club closes sooner, its
                    current period is closed and charged before deletion
                    completes.
                  </li>
                  <li>
                    The audit below shows the count, evidence, rate, projected
                    total and completed charge history throughout the period.
                  </li>
                </ul>
              </details>
            </article>
            )}
          {canFinance && showAnnualAudit && (
          <section className="card club-billing-audit">
            <div className="club-billing-section-heading">
              <div>
                <h3>Annual charge audit</h3>
                <p>
                  See which members are included in the annual charge and why.
                  Offline members qualify after 24 hours in the club.
                </p>
              </div>
              <button
                className="button button-secondary"
                type="button"
                onClick={() => downloadAudit(overview)}
              >
                Download CSV
              </button>
            </div>
            {(overview?.periods || []).length === 0 ? (
              <p className="text-muted">
                No annual billing period has started.
              </p>
            ) : (
              overview.periods.map((period) => (
                <details key={period.id} open={period.status === "accruing"}>
                  <summary>
                    <strong>
                      {dateLabel(period.startsAt)} – {dateLabel(period.endsAt)}
                    </strong>
                    <span>
                      {period.qualifyingMemberCount} members ·{" "}
                      {money(period.amountMinor, period.currency)} ·{" "}
                      {period.status}
                    </span>
                  </summary>
                  <div className="club-billing-period">
                    <p>
                      Rate: {money(period.rateMinor, period.currency)} per
                      qualifying member. Charge date:{" "}
                      {dateLabel(period.chargeAt)}.
                    </p>
                    {period.invoice && (
                      <p>
                        Invoice {period.invoice.number}:{" "}
                        <a
                          href={
                            period.invoice.hostedUrl || period.invoice.pdfUrl
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          View invoice
                        </a>
                      </p>
                    )}
                    {period.attempts?.length > 0 && (
                      <p>
                        {period.attempts.length} collection attempt
                        {period.attempts.length === 1 ? "" : "s"} recorded.
                      </p>
                    )}
                    <AnnualAuditMembers members={period.members} />
                  </div>
                </details>
              ))
            )}
          </section>
          )}
            </section>}
          </section>
        </>
      )}

      {reconciliationOnly &&
        !overview?.locked &&
        team.billingAccess?.locked !== true && (
          <>
            {team.modules?.membershipReconciliation?.enabled === true && (
              <section className="card" id="reconciliation">
                <div className="club-billing-section-heading">
                  <div>
                    <h2>Treasurer Reconciliation</h2>
                    <p>
                      Payment rounds and external membership payment records.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={() => downloadReconciliation(reconciliation)}
                  >
                    Download CSV
                  </button>
                </div>
                <form
                  className="club-billing-round-form"
                  onSubmit={createRound}
                >
                  <input
                    className="form-input"
                    placeholder="Round name"
                    value={roundDraft.name}
                    onChange={(e) =>
                      setRoundDraft({ ...roundDraft, name: e.target.value })
                    }
                    required
                  />
                  <input
                    className="form-input"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Expected amount"
                    value={roundDraft.expectedAmountMajor}
                    onChange={(e) =>
                      setRoundDraft({
                        ...roundDraft,
                        expectedAmountMajor: e.target.value,
                      })
                    }
                  />
                  <input
                    className="form-input"
                    type="date"
                    value={roundDraft.dueDate}
                    onChange={(e) =>
                      setRoundDraft({ ...roundDraft, dueDate: e.target.value })
                    }
                  />
                  <textarea
                    className="form-input"
                    placeholder="Instructions shown to members"
                    value={roundDraft.instructions}
                    onChange={(e) =>
                      setRoundDraft({
                        ...roundDraft,
                        instructions: e.target.value,
                      })
                    }
                  />
                  <button className="button button-primary" disabled={busy}>
                    Create round
                  </button>
                </form>
                <div className="club-billing-rounds">
                  {(reconciliation?.rounds || []).map((round) => (
                    <article key={round.id}>
                      <div>
                        <strong>{round.name}</strong>
                        <small>
                          {round.state} · due {dateLabel(round.dueDate)}
                        </small>
                      </div>
                      <div className="club-billing-record-actions">
                        {round.state !== "open" &&
                          round.state !== "archived" && (
                            <button
                              type="button"
                              onClick={() => setRoundState(round, "open")}
                              disabled={busy}
                            >
                              Open
                            </button>
                          )}
                        {round.state === "open" && (
                          <button
                            type="button"
                            onClick={() => setRoundState(round, "closed")}
                            disabled={busy}
                          >
                            Close
                          </button>
                        )}
                        {round.state !== "archived" && (
                          <button
                            type="button"
                            onClick={() => setRoundState(round, "archived")}
                            disabled={busy}
                          >
                            Archive
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
                <section
                  className="club-billing-assignment"
                  aria-labelledby="round-members-heading"
                >
                  <div className="club-billing-assignment-heading">
                    <div>
                      <h3 id="round-members-heading">People in this round</h3>
                      <p>
                        Pick a round, then add everyone or choose specific
                        people. Family payers are kept together.
                      </p>
                    </div>
                    {assignmentRoundId && (
                      <span className="club-billing-count">
                        {roundRecords.length} added
                      </span>
                    )}
                  </div>
                  <select
                    className="form-input"
                    aria-label="Payment round"
                    value={assignmentRoundId}
                    onChange={(e) => {
                      setAssignmentRoundId(e.target.value);
                      setSelectedCandidateKeys([]);
                      setCandidateSearch("");
                    }}
                  >
                    <option value="">Choose payment round</option>
                    {(reconciliation?.rounds || [])
                      .filter((round) => round.state === "open")
                      .map((round) => (
                        <option key={round.id} value={round.id}>
                          {round.name}
                        </option>
                      ))}
                  </select>
                  {assignmentRoundId && (
                    <>
                      <div className="club-billing-candidate-toolbar">
                        <input
                          className="form-input"
                          type="search"
                          aria-label="Search people to add"
                          placeholder="Search people to add"
                          value={candidateSearch}
                          onChange={(event) =>
                            setCandidateSearch(event.target.value)
                          }
                        />
                        <button
                          type="button"
                          className="button button-primary"
                          disabled={busy || availableCandidates.length === 0}
                          onClick={() =>
                            assignCandidates(
                              availableCandidates.map(
                                (candidate) => candidate.key,
                              ),
                            )
                          }
                        >
                          Add everyone ({availableCandidates.length})
                        </button>
                        <button
                          type="button"
                          className="button button-secondary"
                          disabled={busy || shownCandidates.length === 0}
                          onClick={() =>
                            setSelectedCandidateKeys((current) => [
                              ...new Set([
                                ...current,
                                ...shownCandidates.map(
                                  (candidate) => candidate.key,
                                ),
                              ]),
                            ])
                          }
                        >
                          Select all shown
                        </button>
                        <button
                          type="button"
                          className="button button-secondary"
                          disabled={busy || selectedCandidateKeys.length === 0}
                          onClick={() => setSelectedCandidateKeys([])}
                        >
                          Clear selection
                        </button>
                      </div>
                      <div className="club-billing-candidates">
                        {shownCandidates.length === 0 ? (
                          <p className="club-billing-empty">
                            {availableCandidates.length === 0
                              ? "Everyone eligible is already in this round."
                              : "No people match this search."}
                          </p>
                        ) : (
                          shownCandidates.map((candidate) => (
                            <label key={candidate.key}>
                              <input
                                type="checkbox"
                                checked={selectedCandidateKeys.includes(
                                  candidate.key,
                                )}
                                onChange={() => toggleCandidate(candidate.key)}
                                disabled={busy}
                              />
                              <span>
                                <strong>{candidate.name}</strong>
                                {candidate.coveredMemberCount > 1 && (
                                  <small>
                                    Pays for {candidate.coveredMemberCount}{" "}
                                    members
                                  </small>
                                )}
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                      <div className="club-billing-assignment-actions">
                        <button
                          type="button"
                          className="button button-primary"
                          disabled={busy || selectedCandidateKeys.length === 0}
                          onClick={() =>
                            assignCandidates(selectedCandidateKeys)
                          }
                        >
                          Add selected ({selectedCandidateKeys.length})
                        </button>
                        <button
                          type="button"
                          className="button button-danger"
                          disabled={
                            busy || outstandingRoundRecords.length === 0
                          }
                          onClick={removeOutstandingAssignments}
                        >
                          Remove all outstanding (
                          {outstandingRoundRecords.length})
                        </button>
                      </div>
                      <p className="club-billing-assignment-note">
                        Removing assignments never deletes paid or waived
                        records.
                      </p>
                    </>
                  )}
                </section>
                <div className="club-billing-filters">
                  <input
                    className="form-input"
                    type="search"
                    placeholder="Search members"
                    value={recordSearch}
                    onChange={(event) => setRecordSearch(event.target.value)}
                  />
                  <select
                    className="form-input"
                    value={recordFilter}
                    onChange={(event) => setRecordFilter(event.target.value)}
                  >
                    <option value="all">All records</option>
                    <option value="outstanding">Outstanding</option>
                    <option value="paid">Paid</option>
                    <option value="waived">Waived</option>
                  </select>
                </div>
                <div className="club-billing-records">
                  {visibleRecords.map((record) => {
                    const draft = recordDrafts[record.key] || {};
                    const updateDraft = (patch) =>
                      setRecordDrafts((current) => ({
                        ...current,
                        [record.key]: { ...current[record.key], ...patch },
                      }));
                    return (
                      <article key={record.key}>
                        <div>
                          <strong>{record.memberName}</strong>
                          <small>
                            {record.coveredMemberCount > 1
                              ? `${record.coveredMemberCount} covered members · `
                              : ""}
                            {money(
                              record.expectedAmountMinor,
                              reconciliation?.rounds?.find(
                                (round) => round.id === record.roundId,
                              )?.currency || "GBP",
                            )}
                          </small>
                        </div>
                        <span
                          className={`club-payment-status club-payment-status--${record.status}`}
                        >
                          {record.status}
                        </span>
                        <details className="club-billing-record-detail">
                          <summary>Reconcile</summary>
                          <div className="club-billing-record-fields">
                            <input
                              className="form-input"
                              type="number"
                              min="0"
                              step="0.01"
                              aria-label="Amount paid"
                              placeholder="Amount paid"
                              value={
                                (draft.paidAmountMajor ??
                                  record.paidAmountMinor / 100) ||
                                ""
                              }
                              onChange={(e) =>
                                updateDraft({ paidAmountMajor: e.target.value })
                              }
                            />
                            <input
                              className="form-input"
                              type="date"
                              aria-label="Paid date"
                              value={draft.paidAt || ""}
                              onChange={(e) =>
                                updateDraft({ paidAt: e.target.value })
                              }
                            />
                            <input
                              className="form-input"
                              aria-label="Payment method"
                              placeholder="Method, e.g. bank transfer"
                              value={draft.method ?? record.method}
                              onChange={(e) =>
                                updateDraft({ method: e.target.value })
                              }
                            />
                            <input
                              className="form-input"
                              aria-label="Payment reference"
                              placeholder="Reference"
                              value={draft.reference ?? record.reference}
                              onChange={(e) =>
                                updateDraft({ reference: e.target.value })
                              }
                            />
                            <textarea
                              className="form-input"
                              aria-label="Private administrator note"
                              placeholder="Private admin note"
                              value={draft.adminNote ?? record.adminNote}
                              onChange={(e) =>
                                updateDraft({ adminNote: e.target.value })
                              }
                            />
                            <div className="club-billing-record-actions">
                              <button
                                type="button"
                                onClick={() => mark(record, "paid")}
                                disabled={busy}
                              >
                                Mark paid
                              </button>
                              <button
                                type="button"
                                onClick={() => mark(record, "waived")}
                                disabled={busy}
                              >
                                Waive
                              </button>
                              <button
                                type="button"
                                onClick={() => mark(record, "unpaid")}
                                disabled={busy}
                              >
                                Mark unpaid
                              </button>
                            </div>
                            {record.history?.length > 0 && (
                              <ul className="club-billing-history">
                                {record.history.map((event, index) => (
                                  <li key={`${event.at}-${index}`}>
                                    {event.action.replaceAll("_", " ")} by{" "}
                                    {event.actorName} · {dateLabel(event.at)}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </details>
                      </article>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
      {reconciliationOnly &&
        team.billingAccess?.locked !== true &&
        team.modules?.membershipReconciliation?.enabled !== true && (
          <section className="card">
            <h2>Reconciliation is not enabled</h2>
            <p className="text-muted">
              A full club admin can enable Treasurer Reconciliation from the
              club’s Enabled Modules card.
            </p>
          </section>
        )}
    </main>
    {cutoverOpen && (
      <CutoverWizard
        fromMode={handling.mode}
        toMode={mode}
        readiness={cutoverReadiness}
        loading={cutoverLoading || stripeSetupLoading}
        error={cutoverError}
        effectiveAt={effectiveAt}
        busy={busy}
        onEffectiveAtChange={setEffectiveAt}
        onRefresh={checkCutoverReadiness}
        onConnectStripe={connectStripe}
        onEditPricing={() => {
          setPricingEditorMode(mode);
          setPricingSetupForCutover(true);
          setCutoverOpen(false);
          window.requestAnimationFrame(() =>
            document.getElementById("membership-plans-and-fees")?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            }),
          );
        }}
        onSetUpBillingCard={() => {
          setCutoverOpen(false);
          window.requestAnimationFrame(() =>
            document.getElementById("annual-billing-card")?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            }),
          );
        }}
        onClose={() => {
          if (!busy) setCutoverOpen(false);
        }}
        onConfirm={() => saveMode({ guidedCutover: true })}
      />
    )}
    </>
  );
}
